import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mustChange?: boolean;
}

export function ChangePasswordDialog({ open, onOpenChange, mustChange = false }: ChangePasswordDialogProps) {
  const { toast } = useToast();

  const form = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const res = await apiRequest("PUT", "/api/auth/password", data);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to change password");
      }
      return await res.json();
    },
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Your password has been changed successfully.",
      });
      form.reset();
      
      // Refresh auth state by invalidating and refetching the user query
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      // Wait for refetch to complete before closing to avoid dialog flickering
      await queryClient.refetchQueries({ queryKey: ["/api/auth/me"], type: "active" });
      
      // Small delay to ensure state propagates through React
      await new Promise(resolve => setTimeout(resolve, 100));
      
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to change password",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={mustChange ? undefined : onOpenChange}
    >
      <DialogContent 
        className="sm:max-w-[425px]" 
        data-testid="dialog-change-password"
        onInteractOutside={mustChange ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={mustChange ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {mustChange ? "Change Your Temporary Password" : "Change Password"}
          </DialogTitle>
          <DialogDescription>
            {mustChange 
              ? "For security reasons, you must change your temporary password before continuing."
              : "Enter your current password and choose a new one."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Password</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      type="password" 
                      data-testid="input-current-password"
                      placeholder="Enter current password" 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      type="password" 
                      data-testid="input-new-password"
                      placeholder="At least 8 characters" 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm New Password</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      type="password" 
                      data-testid="input-confirm-password"
                      placeholder="Re-enter new password" 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              {!mustChange && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={changePasswordMutation.isPending}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={changePasswordMutation.isPending || changePasswordMutation.isSuccess}
                data-testid="button-submit"
              >
                {changePasswordMutation.isPending ? "Changing..." : 
                 changePasswordMutation.isSuccess ? "Success! Updating..." :
                 "Change Password"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
